import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import copy from 'clipboard-copy';
import { notification } from 'antd';
import { pageTitle, tabRail, cardShell, fieldStyle, fieldLabel, StatusPill } from '../app/glass/styles';
import Hoverable from '../app/glass/Hoverable';
import GlassSelect from '../app/glass/GlassSelect';
import { buildFlexShipPayload, formatFlexShipAmountInput, isFlexShipEnabled } from '../app/utils/flexShip';
import { getKeys, addKey, getNotificationSettings, updateNotificationSettings, getSddSettings, getFlexShipSettings, updateFlexShipSettings } from '../app/store/actions/dataActions';

const TABS = [
  ['shopify', 'navigation.shopifyIntegration'],
  ['pricing', 'settings.pricingPlan'],
  ['user', 'profile.userInfo'],
  ['brand', 'profile.brandInfo'],
  ['subs', 'profile.subscriptions'],
  ['notif', 'profile.notificationSettings'],
  ['confirm', 'settings.confirmIt'],
];

const Flag = ({ on, label }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid var(--s08)' }}>
    <span style={{ fontSize: 14 }}>{label}</span>
    <span style={{ fontSize: 12, fontWeight: 600, color: on ? '#7dcea0' : 'var(--mut)' }}>{on ? 'ON' : 'OFF'}</span>
  </div>
);

export function SettingsPage({
  user, apiKey, isLoading, notificationSettings, isWaiting, flexShipSettings,
  getKeys, addKey, getNotificationSettings, updateNotificationSettings, getSddSettings,
  getFlexShipSettings, updateFlexShipSettings,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = TABS.some(([k]) => k === tabParam) ? tabParam : 'shopify';
  const vendor = user?.vendor || {};
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState({
    sendDailyDigestNotifications: false,
    sendWeeklyDigestNotifications: false,
    dailyDigestNotificationsTypes: [],
    sendTransactionNotifications: false,
    sendNotificationsOnEmail: false,
  });
  const [flexShipEnabled, setFlexShipEnabled] = useState(false);
  const [flexShipAmount, setFlexShipAmount] = useState('');
  const [flexShipAmountError, setFlexShipAmountError] = useState('');
  const [flexShipLoadState, setFlexShipLoadState] = useState('idle');

  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (tab === 'shopify') getKeys({});
    if (tab === 'notif') getNotificationSettings();
    if (tab === 'subs') getSddSettings();
  }, [tab]);

  useEffect(() => {
    if (tab !== 'brand') return undefined;
    let active = true;
    setFlexShipLoadState((current) => (current === 'ready' ? current : 'loading'));
    Promise.resolve(getFlexShipSettings()).then((result) => {
      if (!active) return;
      setFlexShipLoadState(result === false ? 'error' : 'ready');
    });
    return () => {
      active = false;
    };
  }, [tab, getFlexShipSettings]);

  useEffect(() => {
    if (!notificationSettings) return;
    setSettings({
      sendDailyDigestNotifications: notificationSettings.sendDailyDigestNotifications ?? false,
      sendWeeklyDigestNotifications: notificationSettings.sendWeeklyDigestNotifications ?? false,
      dailyDigestNotificationsTypes: notificationSettings.dailyDigestNotificationsTypes || [],
      sendTransactionNotifications: notificationSettings.sendTransactionNotifications ?? false,
      sendNotificationsOnEmail: notificationSettings.sendNotificationsOnEmail ?? false,
    });
  }, [notificationSettings]);

  useEffect(() => {
    if (!flexShipSettings) return;
    setFlexShipEnabled(isFlexShipEnabled(flexShipSettings.flexShipEnabled));
    setFlexShipAmount(formatFlexShipAmountInput(flexShipSettings.flexShipCollectionAmount));
    setFlexShipAmountError('');
  }, [flexShipSettings]);

  const shippingService = flexShipSettings?.shippingService || (flexShipLoadState === 'error' ? vendor.shippingService : '') || '';
  const flexShipKnown = flexShipLoadState === 'ready' || flexShipLoadState === 'error';
  const flexShipIsBosta = shippingService === 'Bosta';

  const saveFlexShip = () => {
    if (!flexShipIsBosta || isWaiting) return;
    const result = buildFlexShipPayload(flexShipEnabled, flexShipAmount);
    if (result.error) {
      setFlexShipAmountError(result.error);
      notification.error({
        message: t(result.error === 'required' ? 'settings.flexShip.amountRequired' : 'settings.flexShip.amountInvalid'),
      });
      return;
    }
    setFlexShipAmountError('');
    updateFlexShipSettings(result.payload);
  };

  const SwitchRow = ({ checked, onChange, label }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--s08)' }}>
      <span style={{ fontSize: 14 }}>{label}</span>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 42, height: 24, borderRadius: 999, cursor: 'pointer', position: 'relative',
          background: checked ? 'linear-gradient(90deg, #7D010C, #c0303e)' : 'var(--s08)',
          border: '1px solid var(--s12)',
        }}
      >
        <div style={{ position: 'absolute', top: 2, left: checked ? 20 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .18s ease' }} />
      </div>
    </div>
  );

  return (
    <>
      <div className="cz-wrap" style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 6 }}>
        <div style={pageTitle}>{t('navigation.settings')}</div>
      </div>
      <div className="cz-scroll-x" style={tabRail}>
        {TABS.map(([key, label]) => (
          <button key={key} type="button" className={`cz-tab ${tab === key ? 'on' : ''}`} onClick={() => setTab(key)}>
            {t(label)}
          </button>
        ))}
      </div>

      {tab === 'shopify' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={cardShell}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{t('keys.title', { defaultValue: 'API Keys' })}</div>
              <Hoverable as="button" type="button" onClick={() => addKey({})} base={{ marginInlineStart: 'auto' }} className="cz-chip red" hover={{}}>
                {t('keys.addKey', { defaultValue: 'New Key' })}
              </Hoverable>
            </div>
            {isLoading && <div style={{ color: 'var(--mut)' }}>{t('common.loading')}</div>}
            {apiKey?.value ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1.6fr 90px', gap: 12, fontSize: 13 }}>
                <div style={{ color: 'var(--mut)' }}>{apiKey._id}</div>
                <div>{apiKey.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{apiKey.value}</span>
                  <Hoverable
                    as="button"
                    type="button"
                    className="cz-chip red"
                    hover={{}}
                    onClick={() => { copy(apiKey.value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                    base={{ flexShrink: 0, padding: '6px 12px', fontSize: 12 }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <rect x="9" y="9" width="13" height="13" rx="2" />
                      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                    </svg>
                    {copied ? t('common.success') : t('keys.copyKey', { defaultValue: 'Copy Key' })}
                  </Hoverable>
                </div>
                <StatusPill status={apiKey.active ? 'Active' : 'Inactive'}>{apiKey.active ? 'ACTIVE' : 'INACTIVE'}</StatusPill>
              </div>
            ) : (
              <div style={{ color: 'var(--mut)' }}>{t('common.noData')}</div>
            )}
          </div>
          <div style={cardShell}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>{t('keys.integrationInstructions')}</div>
            <ol
              style={{
                margin: 0,
                padding: 0,
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                color: 'var(--tx2)',
                fontSize: 13.5,
                lineHeight: 1.65,
              }}
            >
              {[
                <>
                  <strong>{t('keys.install')}</strong>{' '}
                  {t('keys.installDescription')}{' '}
                  <a
                    className="a2"
                    href="https://apps.shopify.com/chainz-warehousing"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--red-tx)', fontWeight: 600 }}
                  >
                    {t('keys.shopifyAppStore')}
                  </a>
                </>,
                <>
                  {t('keys.step2Description')}{' '}
                  <strong>{t('keys.apiKeyInHomePage')}</strong>
                  {', '}
                  {t('keys.step2Description2')}
                  {apiKey?.value ? (
                    <>
                      {' '}
                      <span
                        style={{ cursor: 'pointer', color: 'var(--red-tx)', fontWeight: 600 }}
                        onClick={() => { copy(apiKey.value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                      >
                        {t('keys.copyKey')}
                      </span>
                    </>
                  ) : null}
                </>,
                <>
                  {t('keys.step3Description')}{' '}
                  <strong>{t('keys.syncProductsPage')}</strong>
                  {', '}
                  {t('keys.step3Description2')}{' '}
                  <strong>{t('keys.sendToChainz')}</strong>.
                </>,
                <>
                  <strong>{t('keys.congratulations')}</strong>{' '}
                  {t('keys.step4Description')}
                </>,
              ].map((content, index) => (
                <li key={index} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span
                    style={{
                      flexShrink: 0,
                      width: 26,
                      height: 26,
                      borderRadius: 9,
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      color: 'var(--red-tx)',
                      background: 'rgba(125,1,12,0.28)',
                      border: '1px solid rgba(190,50,62,0.40)',
                    }}
                  >
                    {index + 1}
                  </span>
                  <span style={{ paddingTop: 2 }}>{content}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {tab === 'pricing' && (
        <div style={cardShell}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{t('settings.pricingPlan')}</div>
          <p style={{ color: 'var(--tx2)', fontSize: 13.5, lineHeight: 1.6 }}>
            {t('settings.pricingNote')}
          </p>
        </div>
      )}

      {tab === 'user' && (
        <div style={cardShell}>
          <div style={fieldLabel}>{t('common.name')}</div>
          <div style={{ ...fieldStyle, marginBottom: 14 }}>{user?.name || '—'}</div>
          <div style={fieldLabel}>{t('common.email')}</div>
          <div style={{ ...fieldStyle, marginBottom: 14 }}>{user?.email || '—'}</div>
          <div style={fieldLabel}>{t('profile.preferredLanguage')}</div>
          <GlassSelect
            value={i18n.language?.startsWith('ar') ? 'ar' : 'en'}
            onChange={(lang) => i18n.changeLanguage(lang)}
            ariaLabel={t('language.label', { defaultValue: 'Language' })}
            options={[
              { value: 'en', label: t('language.english') },
              { value: 'ar', label: t('language.arabic') },
            ]}
          />
        </div>
      )}

      {tab === 'brand' && (
        <div style={cardShell}>
          <div className="cz-g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div style={fieldLabel}>{t('profile.brandName')}</div>
              <div style={fieldStyle}>{vendor.brandName || '—'}</div>
            </div>
            <div>
              <div style={fieldLabel}>{t('common.email')}</div>
              <div style={fieldStyle}>{vendor.email || '—'}</div>
            </div>
            <div>
              <div style={fieldLabel}>{t('profile.primaryColor')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: vendor.primaryColor || '#fff', border: '1px solid var(--s12)' }} />
                <span>{vendor.primaryColor || '—'}</span>
              </div>
            </div>
            <div>
              <div style={fieldLabel}>{t('profile.secondaryColor')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: vendor.secondaryColor || '#7D010C', border: '1px solid var(--s12)' }} />
                <span>{vendor.secondaryColor || '—'}</span>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 16, fontSize: 12, color: 'var(--mut)' }}>{t('profile.shippingService')}: {vendor.shippingService || '—'}</div>
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--mut)' }}>{t('profile.returnPolicyDays')}: {vendor.returnPolicyDays ?? '—'}</div>
          {vendor.returnPolicy && <p style={{ marginTop: 12, fontSize: 13, color: 'var(--tx2)', whiteSpace: 'pre-wrap' }}>{vendor.returnPolicy}</p>}
          <Flag on={vendor.allowToOpenPackage} label={t('profile.allowToOpenPackage')} />
          <Flag on={vendor.fragileOrders} label={t('profile.fragileOrders')} />
          <Flag on={vendor.receiveOrders} label={t('profile.receiveOrders')} />
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--s08)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: '12px 0 4px' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{t('settings.flexShip.title')}</span>
              {flexShipKnown && (
                <div
                  role="switch"
                  aria-checked={flexShipIsBosta ? flexShipEnabled : false}
                  aria-disabled={!flexShipIsBosta}
                  aria-label={t('settings.flexShip.title')}
                  onClick={() => {
                    if (!flexShipIsBosta) return;
                    setFlexShipEnabled((on) => !on);
                    setFlexShipAmountError('');
                  }}
                  style={{
                    width: 42, height: 24, borderRadius: 999, position: 'relative', flexShrink: 0,
                    cursor: flexShipIsBosta ? 'pointer' : 'not-allowed',
                    opacity: flexShipIsBosta ? 1 : 0.45,
                    background: flexShipIsBosta && flexShipEnabled ? 'linear-gradient(90deg, #7D010C, #c0303e)' : 'var(--s08)',
                    border: '1px solid var(--s12)',
                  }}
                >
                  <div style={{ position: 'absolute', top: 2, insetInlineStart: flexShipIsBosta && flexShipEnabled ? 20 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'inset-inline-start .18s ease' }} />
                </div>
              )}
            </div>
            {!flexShipKnown && (
              <div style={{ fontSize: 12.5, color: 'var(--mut)', paddingBottom: 8 }}>{t('common.loading')}</div>
            )}
            {flexShipKnown && flexShipIsBosta && (
              <>
                {flexShipEnabled && (
                  <div style={{ marginTop: 10 }}>
                    <label htmlFor="flexship-amount" style={{ ...fieldLabel, display: 'block' }}>{t('settings.flexShip.amountLabel')}</label>
                    <input
                      id="flexship-amount"
                      value={flexShipAmount}
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="0.00"
                      onChange={(event) => {
                        const next = event.target.value;
                        if (next === '' || /^\d*\.?\d{0,2}$/.test(next)) {
                          setFlexShipAmount(next);
                          setFlexShipAmountError('');
                        }
                      }}
                      style={{
                        ...fieldStyle,
                        boxSizing: 'border-box',
                        borderColor: flexShipAmountError ? 'rgba(190,50,62,0.75)' : undefined,
                      }}
                    />
                    {flexShipAmountError && (
                      <div style={{ color: 'var(--red-tx)', fontSize: 12, marginTop: 6 }}>
                        {t(flexShipAmountError === 'required' ? 'settings.flexShip.amountRequired' : 'settings.flexShip.amountInvalid')}
                      </div>
                    )}
                  </div>
                )}
                <p style={{ margin: '12px 0 0', fontSize: 12.5, lineHeight: 1.6, color: 'var(--tx2)' }}>
                  {t('settings.flexShip.helper')}
                </p>
                <Hoverable
                  as="button"
                  type="button"
                  onClick={saveFlexShip}
                  disabled={isWaiting}
                  className="cz-chip red"
                  base={{ marginTop: 16 }}
                  hover={{}}
                >
                  {isWaiting ? t('common.submitting') : t('common.save')}
                </Hoverable>
              </>
            )}
            {flexShipKnown && !flexShipIsBosta && (
              <p style={{ margin: '8px 0 0', fontSize: 12.5, lineHeight: 1.55, color: 'var(--mut)' }}>
                {t('settings.flexShip.onlyBosta')}
              </p>
            )}
          </div>
        </div>
      )}

      {tab === 'subs' && (
        <div style={cardShell}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--s08)' }}>
            <span>Backtrack</span>
            <Hoverable onClick={() => navigate('/returns-system')} base={{ cursor: 'pointer' }} hover={{ color: 'var(--red-tx)' }}>
              <StatusPill status={vendor.hasReturnSystem ? 'Active' : 'Inactive'}>{vendor.hasReturnSystem ? 'Active' : 'Inactive'}</StatusPill>
            </Hoverable>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--s08)' }}>
            <span>Chainz Pulse</span>
            <Hoverable onClick={() => navigate('/pulse')} base={{ cursor: 'pointer' }} hover={{ color: 'var(--red-tx)' }}>
              <StatusPill status={vendor.hasPulseUnlimited ? 'Active' : 'Inactive'}>{vendor.hasPulseUnlimited ? 'Unlimited' : 'Free'}</StatusPill>
            </Hoverable>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--s08)' }}>
            <span>Same-Day Delivery (Blink)</span>
            <StatusPill status={user?.sddEnabled ? 'Active' : 'Inactive'}>{user?.sddEnabled ? 'Active' : 'Inactive'}</StatusPill>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0' }}>
            <span>Shopify</span>
            <span style={{ color: 'var(--tx2)', fontSize: 13 }}>{user?.shopifyDomain || vendor.shopifyDomain || '—'}</span>
          </div>
        </div>
      )}

      {tab === 'notif' && (
        <div style={cardShell}>
          <SwitchRow checked={settings.sendDailyDigestNotifications} onChange={(v) => setSettings((s) => ({ ...s, sendDailyDigestNotifications: v }))} label={t('profile.dailyDigest')} />
          <SwitchRow checked={settings.sendWeeklyDigestNotifications} onChange={(v) => setSettings((s) => ({ ...s, sendWeeklyDigestNotifications: v }))} label={t('profile.weeklyDigest')} />
          <SwitchRow checked={settings.sendTransactionNotifications} onChange={(v) => setSettings((s) => ({ ...s, sendTransactionNotifications: v }))} label={t('profile.transactionNotifications')} />
          <SwitchRow checked={settings.sendNotificationsOnEmail} onChange={(v) => setSettings((s) => ({ ...s, sendNotificationsOnEmail: v }))} label={t('profile.emailNotifications')} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            {['Orders', 'Inbounds', ...(vendor.hasReturnSystem ? ['Backtrack'] : [])].map((type) => {
              const on = (settings.dailyDigestNotificationsTypes || []).includes(type);
              return (
                <button
                  key={type}
                  type="button"
                  className={`cz-chip ${on ? 'on' : ''}`}
                  onClick={() =>
                    setSettings((s) => {
                      const types = s.dailyDigestNotificationsTypes || [];
                      return { ...s, dailyDigestNotificationsTypes: types.includes(type) ? types.filter((x) => x !== type) : [...types, type] };
                    })
                  }
                >
                  {type}
                </button>
              );
            })}
          </div>
          <Hoverable as="button" onClick={() => updateNotificationSettings(settings)} className="cz-chip red" base={{ marginTop: 18 }} hover={{}}>
            {isWaiting ? t('common.submitting') : t('common.save')}
          </Hoverable>
        </div>
      )}

      {tab === 'confirm' && (
        <div style={cardShell}>
          <Flag on={vendor.usesConfirmIt} label={t('settings.usesConfirmIt')} />
          <Flag on={vendor.sendsOrdersToConfirmIt} label={t('settings.sendsOrders')} />
          <div style={{ ...fieldLabel, marginTop: 16 }}>UUID</div>
          <div style={fieldStyle}>{vendor.confirmItUUID || '—'}</div>
          {vendor.confirmItUUID && (
            <Hoverable as="button" onClick={() => navigate('/confirmit-dashboard')} className="cz-chip red" base={{ marginTop: 16 }} hover={{}}>
              {t('settings.openDashboard')}
            </Hoverable>
          )}
        </div>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  apiKey: state?.records?.key,
  isLoading: state?.records?.isLoading,
  isWaiting: state?.wait?.isLoading,
  notificationSettings: state?.records?.notificationSettings,
  flexShipSettings: state?.records?.flexShipSettings,
});

export default connect(mapStateToProps, {
  getKeys, addKey, getNotificationSettings, updateNotificationSettings, getSddSettings,
  getFlexShipSettings, updateFlexShipSettings,
})(SettingsPage);
